import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/es';
import { Calendar, momentLocalizer, Navigate, Views } from 'react-big-calendar';
import TimeGrid from 'react-big-calendar/lib/TimeGrid';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import ScheduleRoundedIcon from '@material-ui/icons/ScheduleRounded';
import RoomRoundedIcon from '@material-ui/icons/RoomRounded';
import PersonRoundedIcon from '@material-ui/icons/PersonRounded';
import CategoryRoundedIcon from '@material-ui/icons/CategoryRounded';
import DateRangeRoundedIcon from '@material-ui/icons/DateRangeRounded';
import AlarmRoundedIcon from '@material-ui/icons/AlarmRounded';
import PlayCircleFilledRoundedIcon from '@material-ui/icons/PlayCircleFilledRounded';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import EventRoundedIcon from '@material-ui/icons/EventRounded';
import VideocamRoundedIcon from '@material-ui/icons/VideocamRounded';
import AppBarModal from '../general/AppBarModal';
import { coloresEmma } from '../../global/js/funciones';

const localizer = momentLocalizer(moment);

const PALETA = ['#595959', '#C9A227', '#A5A5A5', '#FCD116', '#CCCCCC', '#078930'];

const ESTILOS_CALENDARIO = `
  .emma-horario-calendario .rbc-time-view {
    border: 1px solid ${coloresEmma.fondocolor};
    border-radius: 16px;
    overflow: hidden;
  }
  .emma-horario-calendario .rbc-time-header-content {
    background-color: #fff;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }
  .emma-horario-calendario .rbc-time-header-content::-webkit-scrollbar {
    display: none;
  }
  .emma-horario-calendario .rbc-header {
    position: relative;
    padding: 10px 4px;
    font-weight: 900;
    font-size: 13.5px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${coloresEmma.primarycolor};
    -webkit-text-stroke: 0.5px ${coloresEmma.primarycolor};
    background: transparent;
    border-bottom: 1px solid ${coloresEmma.fondocolor};
    border-left: 1px solid ${coloresEmma.fondocolor};
  }
  .emma-horario-calendario .rbc-header:first-child {
    border-left: none;
  }
  .emma-horario-calendario .rbc-allday-cell {
    display: none;
  }
  .emma-horario-calendario .rbc-today {
    background-color: transparent;
  }
  .emma-horario-calendario .rbc-time-content,
  .emma-horario-calendario .rbc-day-slot .rbc-time-slot,
  .emma-horario-calendario .rbc-timeslot-group {
    border-color: #f0f0f0;
  }
  .emma-horario-calendario .rbc-time-gutter .rbc-timeslot-group {
    border-color: transparent;
  }
  .emma-horario-calendario .rbc-time-header-gutter {
    border-bottom: 1px solid ${coloresEmma.fondocolor};
    background-color: #fff;
  }
  .emma-horario-calendario .rbc-time-gutter,
  .emma-horario-calendario .rbc-time-header-gutter {
    width: 52px !important;
    min-width: 52px !important;
    max-width: 52px !important;
    flex: 0 0 52px !important;
  }
  .emma-horario-calendario .rbc-time-gutter {
    position: sticky;
    left: 0;
    z-index: 5;
    background-color: #fff;
  }
  .emma-horario-calendario .rbc-time-content {
    overflow-y: scroll !important;
    overflow-x: auto !important;
  }
  @media (max-width: 480px) {
    .emma-horario-calendario .rbc-header {
      padding: 8px 2px;
      font-size: 11px;
      letter-spacing: 0.2px;
    }
  }
  .emma-horario-calendario .rbc-time-header-content .rbc-header,
  .emma-horario-calendario .rbc-day-slot {
    flex: 0 0 var(--emma-col-width, auto) !important;
    width: var(--emma-col-width, auto) !important;
  }
  .emma-horario-calendario .rbc-day-slot .rbc-event,
  .emma-horario-calendario .rbc-day-slot .rbc-background-event {
    border: none;
  }
  .emma-horario-calendario .rbc-event-label {
    display: none;
  }
  .emma-horario-calendario .rbc-current-time-indicator {
    display: none;
  }
`;

const DIAS_COMPLETOS = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function formatearFechaLarga(fecha) {
  const d = moment(fecha);
  return `${DIAS_COMPLETOS[d.day()]} ${d.date()} de ${MESES[d.month()]}`;
}

function numDiaHoy() {
  const numDia = moment().isoWeekday() + 1;
  return Math.min(7, Math.max(2, numDia));
}

function textoProximaClase(evento, ahora) {
  const diferenciaMin = Math.max(0, Math.round((evento.start.getTime() - ahora.getTime()) / 60000));
  const horas = Math.floor(diferenciaMin / 60);
  const minutos = diferenciaMin % 60;
  if (horas > 0) return `Proxima clase en ${horas} h ${minutos} min`;
  return `Proxima clase en ${minutos} min`;
}

class SemanaFija extends React.Component {
  render() {
    const { date, ...props } = this.props;
    return <TimeGrid {...props} range={SemanaFija.range(date)} eventOffset={15} />;
  }
}
SemanaFija.range = (date) => {
  const inicio = moment(date).startOf('isoWeek');
  return [0, 1, 2, 3, 4, 5].map((i) => moment(inicio).add(i, 'days').toDate());
};
SemanaFija.navigate = (date, action) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return moment(date).subtract(1, 'week').toDate();
    case Navigate.NEXT:
      return moment(date).add(1, 'week').toDate();
    default:
      return date;
  }
};
SemanaFija.title = (date) => {
  const rango = SemanaFija.range(date);
  return `${moment(rango[0]).format('DD MMM')} - ${moment(rango[rango.length - 1]).format('DD MMM')}`;
};

function textoSobre(hexColor) {
  const hex = (hexColor || '#595959').replace('#', '');
  const canal = (v) => {
    const c = parseInt(v, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminancia = 0.2126 * canal(hex.substring(0, 2)) + 0.7152 * canal(hex.substring(2, 4)) + 0.0722 * canal(hex.substring(4, 6));
  return luminancia > 0.179 ? '#000000' : '#ffffff';
}

function horaValida(valor) {
  const texto = (valor || '').toString().trim();
  const partes = texto.split(':');
  if (partes.length < 2) return null;
  const h = parseInt(partes[0], 10);
  const m = parseInt(partes[1], 10);
  if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
}

function mapearEventos(horarios, anclaLunes) {
  const colores = {};
  const eventos = [];

  (horarios || []).forEach((fila) => {
    const numDia = parseInt(fila.num_dia, 10);
    if (isNaN(numDia) || numDia < 2 || numDia > 7) return;

    const inicioHora = horaValida(fila.hora_inicio);
    const finHora = horaValida(fila.hora_fin);
    if (!inicioHora || !finHora) return;

    const dia = moment(anclaLunes).add(numDia - 2, 'days');
    const inicio = moment(dia).set({ hour: inicioHora.h, minute: inicioHora.m, second: 0 }).toDate();
    const fin = moment(dia).set({ hour: finHora.h, minute: finHora.m, second: 0 }).toDate();
    if (fin <= inicio) return;

    const detalle = fila.detalle || {};
    const llaveColor = (detalle.codigo || fila.asignatura || '').toString();
    if (llaveColor && !colores[llaveColor]) {
      colores[llaveColor] = PALETA[Object.keys(colores).length % PALETA.length];
    }

    eventos.push({
      title: (fila.asignatura || '').toString(),
      num_dia: numDia,
      start: inicio,
      end: fin,
      resource: detalle,
      color: colores[llaveColor] || PALETA[0],
    });
  });

  return eventos;
}

const NOMBRES_CORTOS = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

const EncabezadoDia = ({ date }) => {
  const esHoy = moment(date).isSame(moment(), 'day');
  const texto = `${NOMBRES_CORTOS[moment(date).day()]} ${moment(date).format('DD')}`;
  if (!esHoy) return <span>{texto}</span>;
  return (
    <span
      style={{
        display: 'inline-block',
        maxWidth: '100%',
        boxSizing: 'border-box',
        padding: '3px 8px',
        borderRadius: 20,
        backgroundColor: coloresEmma.primarycolor,
        color: '#fff',
        WebkitTextStroke: '0.4px #fff',
      }}
    >
      {texto}
    </span>
  );
};

const EventoHorario = ({ event }) => {
  const detalle = event.resource || {};
  const horaCorta = `${moment(event.start).format('HH:mm')}-${moment(event.end).format('HH:mm')}`;
  const linea = [horaCorta, detalle.codigo, detalle.salon].filter(Boolean).join(' · ');
  return (
    <div style={{ overflow: 'hidden', height: '100%' }}>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          lineHeight: 1.2,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {event.title}
      </div>
      {linea && (
        <div
          style={{
            fontSize: 10,
            opacity: 0.85,
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {linea}
        </div>
      )}
    </div>
  );
};

const EventoDiaCard = ({ event, esProxima, esActual, ahora }) => {
  const detalle = event.resource || {};
  const linea = [
    `${moment(event.start).format('HH:mm')} - ${moment(event.end).format('HH:mm')}`,
    detalle.codigo,
    detalle.grupo,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <div style={{ width: 6, flexShrink: 0, backgroundColor: event.color }} />
      <div style={{ padding: '6px 10px', overflow: 'hidden', minWidth: 0 }}>
        {esActual && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: coloresEmma.primarycolor,
              fontWeight: 700,
              fontSize: 11,
              marginBottom: 2,
            }}
          >
            <PlayCircleFilledRoundedIcon style={{ fontSize: 14 }} />
            En clase
          </div>
        )}
        {esProxima && !esActual && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: coloresEmma.primarycolor,
              fontWeight: 700,
              fontSize: 11,
              marginBottom: 2,
            }}
          >
            <AlarmRoundedIcon style={{ fontSize: 14 }} />
            {textoProximaClase(event, ahora)}
          </div>
        )}
        <div style={{ fontWeight: 700, fontSize: 13.5, color: '#222', lineHeight: 1.2 }}>{event.title}</div>
        {linea && <div style={{ fontSize: 11.5, color: coloresEmma.gray, marginTop: 2 }}>{linea}</div>}
        {detalle.salon && <div style={{ fontSize: 11.5, color: coloresEmma.gray }}>{detalle.salon}</div>}
        {detalle.profesor && <div style={{ fontSize: 11.5, color: coloresEmma.gray }}>{detalle.profesor}</div>}
      </div>
    </div>
  );
};

const Pildora = ({ activo, texto, onClick }) => (
  <div
    onClick={onClick}
    style={{
      cursor: 'pointer',
      userSelect: 'none',
      padding: '6px 16px',
      borderRadius: 20,
      fontWeight: 600,
      fontSize: 13,
      border: `1.2px solid ${activo ? coloresEmma.primarycolor : coloresEmma.fondocolor}`,
      backgroundColor: activo ? coloresEmma.primarycolor : '#fff',
      color: activo ? '#fff' : '#555',
      transition: 'all 150ms',
    }}
  >
    {texto}
  </div>
);

const FilaDetalle = ({ etiqueta, valor, Icono }) => {
  const texto = (valor || '').toString().trim();
  if (!texto) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '10px 0' }}>
      <Icono style={{ fontSize: 20, color: coloresEmma.primarycolor, marginTop: 1, flexShrink: 0 }} />
      <div>
        <Typography
          variant="caption"
          style={{ display: 'block', fontWeight: 700, color: coloresEmma.gray, textTransform: 'uppercase', letterSpacing: 0.4 }}
        >
          {etiqueta}
        </Typography>
        <Typography variant="body2" style={{ color: '#333' }}>
          {texto}
        </Typography>
      </div>
    </div>
  );
};

// Las clases virtuales no traen dia/hora (no se pueden ubicar en la grilla),
// asi se distinguen de una clase normal con horario incompleto por error.
function esClaseVirtual(fila) {
  return !!fila && fila.num_dia == null && !fila.hora_inicio && !fila.hora_fin;
}

const AlertaClaseVirtual = ({ cantidad, onClick }) => (
  <div
    onClick={onClick}
    style={{
      cursor: 'pointer',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      margin: '0 16px 12px',
      padding: '12px 14px',
      borderRadius: 12,
      backgroundColor: coloresEmma.fondocolor,
      border: `1px solid ${coloresEmma.primarycolor}`,
      color: coloresEmma.primarycolor,
    }}
  >
    <EventRoundedIcon style={{ fontSize: 20, flexShrink: 0 }} />
    <span style={{ flex: 1, fontWeight: 700, fontSize: 13.5 }}>
      Tienes {cantidad} clase{cantidad === 1 ? '' : 's'} virtual{cantidad === 1 ? '' : 'es'}
    </span>
    <ChevronRightRoundedIcon />
  </div>
);

const TarjetaClaseVirtual = ({ clase }) => {
  const detalle = clase.detalle || {};
  return (
    <div
      style={{
        border: `1px solid ${coloresEmma.fondocolor}`,
        borderRadius: 14,
        marginBottom: 14,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          backgroundColor: '#fafafa',
          borderBottom: `1px solid ${coloresEmma.fondocolor}`,
        }}
      >
        <VideocamRoundedIcon style={{ color: coloresEmma.primarycolor, fontSize: 20, flexShrink: 0 }} />
        <Typography style={{ fontWeight: 800, fontSize: 13.5, color: '#222' }}>
          {(clase.asignatura || '').trim()}
        </Typography>
      </div>
      <div style={{ padding: '0 16px' }}>
        <FilaDetalle Icono={PersonRoundedIcon} etiqueta="Profesor" valor={detalle.profesor} />
        <FilaDetalle Icono={CategoryRoundedIcon} etiqueta="Tipo" valor={detalle.tipo} />
        <FilaDetalle Icono={DateRangeRoundedIcon} etiqueta="Fechas" valor={detalle.fechas} />
      </div>
    </div>
  );
};

const HorarioCalendario = ({ data, cargando }) => {
  const [vista, setVista] = useState(Views.WEEK);
  const [diaSeleccionado, setDiaSeleccionado] = useState(numDiaHoy());
  const [eventoSel, setEventoSel] = useState(null);
  const [eventoMostrar, setEventoMostrar] = useState(null);
  const [anchoColumna, setAnchoColumna] = useState(null);
  const [dialogVirtuales, setDialogVirtuales] = useState(false);
  const contenedorRef = useRef(null);

  const anclaLunes = useMemo(() => moment().startOf('isoWeek').toDate(), []);
  const eventos = useMemo(() => mapearEventos(data, anclaLunes), [data, anclaLunes]);
  const clasesVirtuales = useMemo(() => (data || []).filter(esClaseVirtual), [data]);
  const ahora = useMemo(() => new Date(), []);

  const claseActual = useMemo(() => {
    return eventos.find((e) => e.start.getTime() <= ahora.getTime() && e.end.getTime() > ahora.getTime()) || null;
  }, [eventos, ahora]);

  const proximaClase = useMemo(() => {
    if (claseActual) return null;
    const futuras = eventos.filter((e) => e.start.getTime() > ahora.getTime());
    futuras.sort((a, b) => a.start.getTime() - b.start.getTime());
    return futuras[0] || null;
  }, [eventos, ahora, claseActual]);

  useEffect(() => {
    if (eventoSel) setEventoMostrar(eventoSel);
  }, [eventoSel]);

  useEffect(() => {
    const contenedor = contenedorRef.current;
    if (!contenedor) return undefined;

    const MIN_ANCHO_COLUMNA_SEMANA = 92;

    const medir = () => {
      const fila = contenedor.querySelector('.rbc-time-header');
      const scrollArea = contenedor.querySelector('.rbc-time-content');
      if (!fila || !scrollArea) return;

      const anchoScrollbar = scrollArea.offsetWidth - scrollArea.clientWidth;
      const numColumnas = vista === Views.DAY ? 1 : 6;
      const disponible = fila.clientWidth - 52 - anchoScrollbar;
      if (disponible <= 0) return;
      const anchoCalculado = disponible / numColumnas;
      setAnchoColumna(vista === Views.WEEK ? Math.max(anchoCalculado, MIN_ANCHO_COLUMNA_SEMANA) : anchoCalculado);
    };

    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(contenedor);
    return () => observer.disconnect();
  }, [vista, eventos]);

  useEffect(() => {
    const contenedor = contenedorRef.current;
    if (!contenedor) return undefined;

    const encabezado = contenedor.querySelector('.rbc-time-header-content');
    const cuerpo = contenedor.querySelector('.rbc-time-content');
    if (!encabezado || !cuerpo) return undefined;

    let sincronizando = false;
    const desdeCuerpo = () => {
      if (sincronizando) return;
      sincronizando = true;
      encabezado.scrollLeft = cuerpo.scrollLeft;
      sincronizando = false;
    };
    const desdeEncabezado = () => {
      if (sincronizando) return;
      sincronizando = true;
      cuerpo.scrollLeft = encabezado.scrollLeft;
      sincronizando = false;
    };

    cuerpo.addEventListener('scroll', desdeCuerpo);
    encabezado.addEventListener('scroll', desdeEncabezado);
    return () => {
      cuerpo.removeEventListener('scroll', desdeCuerpo);
      encabezado.removeEventListener('scroll', desdeEncabezado);
    };
  }, [vista, eventos]);

  const fechaVista = vista === Views.DAY
    ? moment(anclaLunes).add(diaSeleccionado - 2, 'days').toDate()
    : anclaLunes;

  const eventPropGetter = (event) => {
    const color = event.color || PALETA[0];
    return {
      style: {
        backgroundColor: color,
        color: textoSobre(color),
        borderRadius: 8,
        border: 'none',
        padding: '4px 6px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      },
    };
  };

  const eventPropGetterDia = () => ({
    style: {
      backgroundColor: '#fff',
      border: 'none',
      borderRadius: 10,
      padding: 0,
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    },
  });

  const componentesDia = useMemo(
    () => ({
      event: ({ event }) => (
        <EventoDiaCard
          event={event}
          esProxima={event === proximaClase}
          esActual={event === claseActual}
          ahora={ahora}
        />
      ),
    }),
    [proximaClase, claseActual, ahora]
  );

  const formatosCalendario = {
    dayFormat: (fecha) => {
      const nombres = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
      return `${nombres[moment(fecha).day()]} ${moment(fecha).format('DD')}`;
    },
    timeGutterFormat: (fecha) => moment(fecha).format('HH:mm'),
  };

  if (cargando) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <CircularProgress style={{ color: coloresEmma.primarycolor }} />
      </div>
    );
  }

  if (!eventos.length) {
    // Si el estudiante solo tiene clases virtuales no tiene horario fijo
    // (es libre): se ocultan la grilla y los toggles Semana/Dia y se
    // muestran las virtuales como tarjetas en su lugar.
    if (clasesVirtuales.length) {
      return (
        <div style={{ padding: '16px 16px 4px' }}>
          {clasesVirtuales.map((clase, i) => (
            <TarjetaClaseVirtual key={i} clase={clase} />
          ))}
        </div>
      );
    }
    return (
      <div style={{ padding: 40, textAlign: 'center', color: coloresEmma.gray }}>
        No se encontraron clases programadas para el periodo en curso.
      </div>
    );
  }

  return (
    <div>
      {!!clasesVirtuales.length && (
        <AlertaClaseVirtual cantidad={clasesVirtuales.length} onClick={() => setDialogVirtuales(true)} />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 16px' }}>
        <Pildora texto="Semana" activo={vista === Views.WEEK} onClick={() => setVista(Views.WEEK)} />
        <Pildora texto="Dia" activo={vista === Views.DAY} onClick={() => setVista(Views.DAY)} />
      </div>

      {vista === Views.DAY && (
        <div style={{ padding: '0 16px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconButton
            size="small"
            onClick={() => setDiaSeleccionado((d) => Math.max(2, d - 1))}
            disabled={diaSeleccionado <= 2}
            style={{ color: coloresEmma.primarycolor }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <div
            style={{
              flex: 1,
              textAlign: 'center',
              fontWeight: 700,
              fontSize: 14,
              color: coloresEmma.primarycolor,
              backgroundColor: '#fff',
              border: `1px solid ${coloresEmma.fondocolor}`,
              borderRadius: 12,
              padding: '10px 0',
            }}
          >
            {formatearFechaLarga(fechaVista)}
          </div>
          <IconButton
            size="small"
            onClick={() => setDiaSeleccionado((d) => Math.min(7, d + 1))}
            disabled={diaSeleccionado >= 7}
            style={{ color: coloresEmma.primarycolor }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </div>
      )}

      <div
        ref={contenedorRef}
        className="emma-horario-calendario"
        style={{
          height: 640,
          padding: '0 16px 16px',
          ...(anchoColumna ? { '--emma-col-width': `${anchoColumna}px` } : {}),
        }}
      >
        <style>{ESTILOS_CALENDARIO}</style>
        <Calendar
          localizer={localizer}
          culture="es"
          events={eventos}
          date={fechaVista}
          onNavigate={() => {}}
          view={vista}
          onView={setVista}
          views={{ week: SemanaFija, day: true }}
          formats={formatosCalendario}
          onDrillDown={(fecha) => {
            setDiaSeleccionado(moment(fecha).isoWeekday() + 1);
            setVista(Views.DAY);
          }}
          min={moment().set({ hour: 6, minute: 0 }).toDate()}
          max={moment().set({ hour: 22, minute: 0 }).toDate()}
          step={30}
          timeslots={2}
          toolbar={false}
          onSelectEvent={setEventoSel}
          eventPropGetter={vista === Views.DAY ? eventPropGetterDia : eventPropGetter}
          components={vista === Views.DAY ? componentesDia : { event: EventoHorario, header: EncabezadoDia }}
        />
      </div>

      <Dialog
        open={!!eventoSel}
        onClose={() => setEventoSel(null)}
        maxWidth="xs"
        fullWidth
        style={{ zIndex: 1400 }}
        PaperProps={{ style: { borderRadius: 20, overflow: 'hidden' } }}
        TransitionProps={{ onExited: () => setEventoMostrar(null) }}
      >
        {eventoMostrar && (
          <>
            <div
              style={{
                position: 'relative',
                backgroundColor: '#fff',
                color: coloresEmma.primarycolor,
                padding: '20px 48px 12px 24px',
                borderBottom: `1px solid ${coloresEmma.fondocolor}`,
              }}
            >
              <IconButton
                onClick={() => setEventoSel(null)}
                style={{ position: 'absolute', top: 8, right: 8, color: 'inherit' }}
              >
                <CloseRoundedIcon />
              </IconButton>
              <Typography style={{ fontWeight: 800, fontSize: 17, lineHeight: 1.25, color: 'inherit' }}>
                {eventoMostrar.title}
              </Typography>
              {eventoMostrar.resource.grupo && (
                <Typography style={{ fontSize: 13, opacity: 0.85, marginTop: 2, color: 'inherit' }}>
                  Grupo {eventoMostrar.resource.grupo}
                </Typography>
              )}
            </div>
            <DialogContent style={{ padding: '4px 24px 20px' }}>
              <FilaDetalle Icono={ScheduleRoundedIcon} etiqueta="Horario" valor={eventoMostrar.resource.horario} />
              <FilaDetalle Icono={RoomRoundedIcon} etiqueta="Salon" valor={eventoMostrar.resource.salon} />
              <FilaDetalle Icono={PersonRoundedIcon} etiqueta="Profesor" valor={eventoMostrar.resource.profesor} />
              <FilaDetalle Icono={CategoryRoundedIcon} etiqueta="Tipo" valor={eventoMostrar.resource.tipo} />
              <FilaDetalle Icono={DateRangeRoundedIcon} etiqueta="Fechas" valor={eventoMostrar.resource.fechas} />
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={dialogVirtuales}
        onClose={() => setDialogVirtuales(false)}
        maxWidth="xs"
        fullWidth
        style={{ zIndex: 1400 }}
        PaperProps={{ style: { borderRadius: 20, overflow: 'hidden' } }}
      >
        <AppBarModal titulo="Clases Virtuales" mostrarModal={() => setDialogVirtuales(false)} titulo_accion="" accion="" />
        <DialogContent style={{ padding: '16px 24px 20px' }}>
          {clasesVirtuales.map((clase, i) => (
            <TarjetaClaseVirtual key={i} clase={clase} />
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

HorarioCalendario.propTypes = {
  data: PropTypes.array,
  cargando: PropTypes.bool,
};

export default HorarioCalendario;
